import { Image3D } from '../patient/image3d';
import { WindowLevelBounds, windowLevelOnBounds } from '../patient/windowLevel';
import { domainEventBus } from './DomainEventBus';
import { WindowLevelSetEvent } from './WindowingDomainEvents';

/**
 * The level bounds an image's window/level is read against: those of its
 * stored window, or of its default before one is stored. The same pair a
 * typed value is checked against, so what a control accepts and what the
 * bus stores agree.
 */
export function levelBoundsOf(image: Image3D): WindowLevelBounds {
    const { minLevel, maxLevel } =
        image.windowLevel ?? image.getDefaultWindowLevel();
    return { minLevel, maxLevel };
}

/**
 * Publishes an absolute window/level for an image's series, exactly as
 * given. The image's stored level bounds are kept; a level outside them
 * widens the bound it crosses to the one {@link WindowLevel} gives such a
 * level by default, headroom included, so the level is applied as listed
 * and stays draggable. Nothing is clamped.
 *
 * @throws RangeError for a window width of zero or less, or a value that is
 *   not a finite number, as {@link WindowLevel} refuses them; nothing is
 *   published. A fractional width, such as the automatic window of a
 *   floating-point series, is applied as given.
 */
export function publishWindowLevelSet(
    image: Image3D,
    windowWidth: number,
    windowCenter: number
): void {
    if (!Number.isFinite(windowWidth) || !Number.isFinite(windowCenter)) {
        throw new RangeError('A window/level must be a finite number');
    }
    if (windowWidth <= 0) {
        throw new RangeError(
            `A window width must be greater than 0 (got ${windowWidth})`
        );
    }
    domainEventBus.publishSync(
        new WindowLevelSetEvent(
            windowLevelOnBounds(
                windowWidth,
                windowCenter,
                levelBoundsOf(image)
            ),
            image.seriesUid,
            'user'
        )
    );
}
